import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../auth/api'

const API_BASE = '/api'

const MAX_FILES = 5
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_EXTS = ['pdf', 'docx', 'doc', 'txt', 'md', 'jpg', 'jpeg', 'png', 'webp', 'gif']
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'gif']

function formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function getFileIcon(ext) {
    if (ext === 'pdf') return '📕'
    if (['docx', 'doc'].includes(ext)) return '📘'
    return '📄'
}

export default function NewCase() {
    const navigate = useNavigate()
    const [clientName, setClientName] = useState('')
    const [problem, setProblem] = useState('')
    const [language, setLanguage] = useState('auto')
    const [files, setFiles] = useState([])
    const [previews, setPreviews] = useState({})
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [caseId, setCaseId] = useState(null)
    const [caseNumber, setCaseNumber] = useState('')
    const [researching, setResearching] = useState(false)
    const [rounds, setRounds] = useState([])
    const [caseStatus, setCaseStatus] = useState('')
    const [dragActive, setDragActive] = useState(false)
    const [pollInterval, setPollInterval] = useState(null)

    const dropRef = useRef(null)
    const fileInputRef = useRef(null)

    // Poll for research rounds
    const pollRounds = useCallback(async (id) => {
        try {
            const res = await api.get(`/cases/${id}/rounds`)
            const data = res.data
            setRounds(data.rounds || [])
            setCaseStatus(data.status || '')
            if (data.status === 'completed') {
                setResearching(false)
            }
        } catch (e) {
            // ignore
        }
    }, [])

    // Start polling when case is created
    useEffect(() => {
        if (caseId && researching) {
            pollRounds(caseId)
            const interval = setInterval(() => pollRounds(caseId), 3000)
            setPollInterval(interval)
            return () => clearInterval(interval)
        }
    }, [caseId, researching, pollRounds])

    function addFiles(newFiles) {
        const valid = newFiles.filter(f => {
            const ext = f.name.split('.').pop().toLowerCase()
            return ALLOWED_EXTS.includes(ext) && f.size <= MAX_FILE_SIZE
        })
        const combined = [...files, ...valid].slice(0, MAX_FILES)
        setFiles(combined)

        // Generate previews for images
        combined.forEach(file => {
            const ext = file.name.split('.').pop().toLowerCase()
            if (IMAGE_EXTS.includes(ext) && !previews[file.name]) {
                const reader = new FileReader()
                reader.onload = (e) => {
                    setPreviews(prev => ({ ...prev, [file.name]: e.target.result }))
                }
                reader.readAsDataURL(file)
            }
        })
    }

    function handleDrag(e) {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true)
        } else if (e.type === 'dragleave') {
            setDragActive(false)
        }
    }

    function handleDrop(e) {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFiles(Array.from(e.dataTransfer.files))
        }
    }

    function handleFileInput(e) {
        if (e.target.files && e.target.files.length > 0) {
            addFiles(Array.from(e.target.files))
            e.target.value = '' // reset
        }
    }

    function removeFile(index) {
        const removed = files[index]
        const newFiles = files.filter((_, i) => i !== index)
        setFiles(newFiles)
        setPreviews(prev => {
            const copy = { ...prev }
            delete copy[removed.name]
            return copy
        })
    }

    async function handleSubmit(e) {
        e.preventDefault()
        if (!clientName.trim() || !problem.trim()) return

        setSubmitting(true)
        setError('')

        try {
            const formData = new FormData()
            formData.append('client_name', clientName)
            formData.append('problem_description', problem)
            formData.append('language', language)
            files.forEach(f => formData.append('files', f))

            const res = await api.post(`/cases/`, formData, {
                headers: { 'Content-Type': undefined }, // let browser set multipart boundary
            })
            const data = res.data
            setCaseId(data.case_id)
            setCaseNumber(data.case_number)
            setResearching(true)
        } catch (err) {
            setError(err.message)
        } finally {
            setSubmitting(false)
        }
    }

    // Research started view
    if (caseId) {
        // Calculate progress
        const maxRounds = 5
        const completedRounds = Math.min(rounds.length, maxRounds)
        const progress = caseStatus === 'completed' ? 100 : Math.round((completedRounds / maxRounds) * 100)

        return (
            <div className="card">
                <h2>&#128640; Case Research in Progress</h2>
                <p><strong>Case:</strong> {caseNumber} (ID: {caseId})</p>

                {/* Progress bar */}
                <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                        background: 'var(--bg-tertiary)',
                        borderRadius: '8px',
                        height: '24px',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <div style={{
                            background: caseStatus === 'completed'
                                ? 'linear-gradient(90deg, #10b981, #34d399)'
                                : 'linear-gradient(90deg, var(--gold-600), var(--amber-500))',
                            width: progress + '%',
                            height: '100%',
                            transition: 'width 0.5s ease',
                            borderRadius: '8px'
                        }} />
                        <div style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'var(--text-primary)'
                        }}>
                            {progress}% — Round {completedRounds}/{maxRounds}
                        </div>
                    </div>
                </div>

                {researching && (
                    <div className="loading" style={{ padding: '1rem' }}>
                        <div className="spinner"></div>
                        <p>Researching... This typically takes 30-60 seconds.</p>
                    </div>
                )}

                {/* Live rounds feed */}
                {rounds.length > 0 && (
                    <div style={{ marginTop: '1rem', maxHeight: '400px', overflowY: 'auto' }}>
                        {rounds.map((r, i) => {
                            const isResearcher = r.agent === 'researcher'
                            const score = r.score || 0
                            return (
                                <details key={i} style={{
                                    border: `1px solid ${isResearcher ? 'var(--blue-800)' : 'var(--red-800)'}`,
                                    borderRadius: '8px',
                                    marginBottom: '0.5rem',
                                    background: 'var(--bg-tertiary)',
                                    padding: '0.75rem'
                                }}>
                                    <summary style={{
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        color: isResearcher ? 'var(--blue-400)' : 'var(--red-400)'
                                    }}>
                                        {isResearcher ? '🔬' : '🎯'} {r.agent.toUpperCase()} — Round {r.round_number}
                                        {score > 0 && <span style={{ marginLeft: '0.5rem', fontSize: '0.85em' }}>Score: {score.toFixed(0)}%</span>}
                                    </summary>
                                    <div style={{ marginTop: '0.5rem', fontSize: '0.9em', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                                        {(() => {
                                            try {
                                                const content = typeof r.content === 'string' ? JSON.parse(r.content) : r.content
                                                return content.summary || content.score_reasoning || content.self_score_reasoning || JSON.stringify(content, null, 2).slice(0, 500)
                                            } catch {
                                                return ''
                                            }
                                        })()}
                                    </div>
                                </details>
                            )
                        })}
                    </div>
                )}

                {caseStatus === 'completed' && (
                    <div style={{
                        border: '2px solid var(--green-600)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginTop: '1rem',
                        background: 'rgba(34, 197, 94, 0.1)'
                    }}>
                        <h3 style={{ color: 'var(--green-400)' }}>&#9989; Research Complete!</h3>
                        <p>Score: {rounds.filter(r => r.agent === 'critic').pop()?.score?.toFixed(0) || '—'}% • {rounds.filter(r => r.agent === 'critic').length} rounds</p>
                    </div>
                )}

                <div className="flex gap-2" style={{ marginTop: '1rem' }}>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate(`/cases/${caseId}`)}
                    >
                        View Full Brief &#8594;
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => {
                            setClientName('')
                            setProblem('')
                            setFiles([])
                            setPreviews({})
                            setCaseId(null)
                            setCaseNumber('')
                            setResearching(false)
                            setRounds([])
                            setCaseStatus('')
                        }}
                    >
                        New Case
                    </button>
                </div>
            </div>
        )
    }

    // New case form
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            alignItems: 'start'
        }}>
            {/* LEFT PANEL: Input */}
            <div className="card">
                <h2>&#128196; New Research Case</h2>
                <p className="text-muted mb-3">
                    Fill in the details below to start an AI-powered research brief.
                </p>

                {error && (
                    <div style={{ color: 'var(--error)', marginBottom: '1rem' }}>
                        Error: {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="client_name">Client Name *</label>
                        <input
                            id="client_name"
                            type="text"
                            placeholder="e.g. Ministry of Public Security"
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                            required
                        />
                    </div>

                    {/* Language toggle */}
                    <div className="form-group">
                        <label>Language</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {['auto', 'en', 'vi'].map(lang => (
                                <button
                                    key={lang}
                                    type="button"
                                    onClick={() => setLanguage(lang)}
                                    style={{
                                        flex: 1,
                                        padding: '0.5rem',
                                        borderRadius: '6px',
                                        border: language === lang ? '2px solid var(--gold-600)' : '1px solid var(--border-color)',
                                        background: language === lang ? 'rgba(245, 158, 11, 0.15)' : 'transparent',
                                        color: language === lang ? 'var(--gold-400)' : 'var(--text-secondary)',
                                        cursor: 'pointer',
                                        fontSize: '0.85rem',
                                        fontWeight: language === lang ? '600' : '400'
                                    }}
                                >
                                    {lang === 'auto' ? '🔄 Auto' : lang === 'en' ? '🇬🇧 EN' : '🇻🇳 VI'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="problem">Problem Description *</label>
                        <textarea
                            id="problem"
                            placeholder={"Describe your client's security challenge... / Mô tả vấn đề an ninh của khách hàng...\n\nExamples:\n- Drone detection system for border surveillance\n- Body-worn cameras with facial recognition\n- Radio encryption for tactical operations"}
                            rows={8}
                            value={problem}
                            onChange={(e) => setProblem(e.target.value)}
                            required
                        />
                    </div>

                    {/* Attachments */}
                    <div className="form-group">
                        <label>&#128206; Attachments (optional)</label>
                        <div
                            ref={dropRef}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                border: `2px dashed ${dragActive ? 'var(--gold-600)' : 'var(--border-color)'}`,
                                borderRadius: '8px',
                                padding: '1.5rem',
                                textAlign: 'center',
                                cursor: 'pointer',
                                background: dragActive ? 'rgba(245, 158, 11, 0.05)' : 'var(--bg-tertiary)',
                                transition: 'all 0.2s',
                                marginBottom: files.length > 0 ? '0.5rem' : 0
                            }}
                        >
                            <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>&#128206;</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                Drop files here or click to browse
                            </div>
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                Images: JPG, PNG, WEBP • Docs: PDF, DOCX, TXT • Max 5 files • 10MB each
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept=".pdf,.docx,.doc,.txt,.md,.jpg,.jpeg,.png,.webp,.gif"
                                onChange={handleFileInput}
                                style={{ display: 'none' }}
                            />
                        </div>

                        {/* File preview list */}
                        {files.length > 0 && (
                            <div style={{ marginTop: '0.5rem' }}>
                                {files.map((f, i) => {
                                    const ext = f.name.split('.').pop().toLowerCase()
                                    const isImage = IMAGE_EXTS.includes(ext)
                                    return (
                                        <div key={i} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            padding: '0.5rem',
                                            background: 'var(--bg-tertiary)',
                                            borderRadius: '6px',
                                            marginBottom: '0.25rem',
                                            border: '1px solid var(--border-color)'
                                        }}>
                                            {/* Thumbnail or icon */}
                                            {isImage && previews[f.name] ? (
                                                <img src={previews[f.name]} alt="" style={{
                                                    width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px'
                                                }} />
                                            ) : (
                                                <span style={{ fontSize: '1.25rem' }}>{getFileIcon(ext)}</span>
                                            )}
                                            {/* Filename and size */}
                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                <div style={{ fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {f.name}
                                                </div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                                    {formatSize(f.size)}
                                                </div>
                                            </div>
                                            {/* Remove button */}
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); removeFile(i) }}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: 'var(--red-400)',
                                                    cursor: 'pointer',
                                                    fontSize: '1rem',
                                                    padding: '0.25rem'
                                                }}
                                                title="Remove file"
                                            >
                                                &#10005;
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary"
                        style={{
                            width: '100%',
                            background: 'linear-gradient(135deg, var(--gold-600), var(--amber-500))',
                            color: 'var(--bg-primary)',
                            fontWeight: '700',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            fontSize: '1rem'
                        }}
                        disabled={submitting}
                    >
                        {submitting ? 'Creating Case...' : '&#128640; Start Research'}
                    </button>
                </form>
            </div>

            {/* RIGHT PANEL: Info / Help */}
            <div className="card" style={{ opacity: 0.7 }}>
                <h3>&#128161; How It Works</h3>
                <ol style={{ paddingLeft: '1.5rem', lineHeight: 1.8 }}>
                    <li><strong>Debate Engine</strong> runs a multi-round research loop between a Researcher and Critic agent.</li>
                    <li><strong>Knowledge Base</strong> searches LEXIS portfolio for matching products and vendors.</li>
                    <li><strong>Web Research</strong> finds additional solutions and vendor information online.</li>
                    <li><strong>Tender Intelligence</strong> identifies real-world solution patterns from procurement data.</li>
                    <li><strong>Score</strong> increases each round until target quality is reached (max 5 rounds).</li>
                </ol>

                <h3 style={{ marginTop: '1.5rem' }}>&#128206; Attachments</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                    Upload supporting documents to help the AI better understand your client's needs:
                </p>
                <ul style={{ paddingLeft: '1.5rem', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                    <li><strong>PDF / DOCX</strong> — RFP documents, specifications, requirements</li>
                    <li><strong>Images (JPG, PNG, WEBP)</strong> — Photos of equipment to analyze, site photos</li>
                    <li><strong>TXT / MD</strong> — Notes, requirements documents</li>
                </ul>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Max 5 files, 10MB each. Files are analyzed by AI vision (images) or text extraction (documents).
                </p>

                <h3 style={{ marginTop: '1.5rem' }}>&#127760; Language Support</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                    Describe your problem in any language. The system automatically detects Vietnamese or English input and produces bilingual output. Select <strong>Auto</strong> for automatic detection, or force a specific language.
                </p>
            </div>
        </div>
    )
}